import type { InteractionFamily, ScaleCell, ScaleConfig, Tonic } from 'Types/ExerciseContentTypes';
import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { SessionExercise } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import {
  SCALE_RUN_PROMPT,
  scalesPerformanceRead,
} from 'Seeds/ExerciseContent/scalesPerformanceRead';
import type { SeedExercisePackage } from 'Seeds/ExerciseContent/seedTypes';
import type { ExerciseMaterialKey } from 'Phaser/Scenes/OSMDExerciseScene.takeRecord';
import type { EntryContext } from 'Reducers/exerciseProgressReducer';
import { toProgressPolicy, waypointConfig, type ExerciseContent } from './exerciseContentApi';

/**
 * Resolve a tapped (level, tier, key) on the Exercises overview into the concrete
 * sitting the play scene needs (MUS-481). The tap itself is the whole transition:
 * no hand/octave/tempo chooser, the tapped key of the tapped level launched at the
 * tapped tier's waypoint.
 *
 * Sources:
 *   - The structure (the exercise id, levels, cells, tiers, package keys and the two
 *     progress policies) is the server's exercise read, the same content the overview
 *     renders, so the play route resolves the tap against exactly what the user saw.
 *   - The cell is the tapped key of the level, when the level holds it; otherwise the
 *     level's first cell. Every scale in every key can be played.
 *   - A tier IS a full-config waypoint, so its fields map one-to-one onto the
 *     playable `ScaleConfig` (hand / octaves / direction / rhythm / tempo).
 *   - The figure is the tier's package, looked up by its key in the authored seed the
 *     server's content was written from (the read serves the key, not the prompt).
 *   - The engine counts takes against the server's own clearance and mastery
 *     policies, never a local copy.
 */

/**
 * The major scale's stepwise-run figure: the seeded Major packages' own prompt (a
 * major-scale interval walk laid out as one 8/4 bar, meter hidden). Tonic-agnostic -
 * the cell drives spelling and key; this only carries the figure, meter, register
 * anchor, and single-cell traversal. The dev launcher plays it; the real play route
 * reads the same prompt from the tier's package, so the two cannot drift.
 */
export const PLAY_PROMPT: StepwiseRunPrompt = SCALE_RUN_PROMPT;

/**
 * The same figure in 4/4 with the meter drawn: the layout the slice used before
 * time signatures became a layout tool. The dev launcher plays it at
 * `/__visual/exercise-play?meter=four-four`, and the scene specs pin it.
 */
export const FOUR_FOUR_PLAY_PROMPT: StepwiseRunPrompt = {
  ...PLAY_PROMPT,
  time_signature: [4, 4],
  show_time_signature: true,
};

/**
 * The dev launcher's stand-in identity for the scales exercise (`/__visual/exercise-play`).
 * The real play route never uses it: it resolves the exercise, its id and its policies from
 * the server by slug.
 */
export const PLAY_EXERCISE: SessionExercise = {
  id: 1,
  slug: 'scales-performance-read',
  clearance_policy: { type: 'cumulative', n: 3 },
  mastery_policy: { type: 'streak', n: 3 },
};

/** The concrete sitting the scene launches: figure + exercise identity + the
 *  content coordinate (cell) + the frontier configuration in play (config) + the
 *  interaction family that binds it to a playable scene (MUS-456). */
export interface ResolvedPlaySession {
  prompt: StepwiseRunPrompt;
  exercise: SessionExercise;
  cell: ScaleCell;
  config: ScaleConfig;
  interaction_family: InteractionFamily;
  /** The package, level and tier the sitting plays: stored with every take. */
  materialKey: ExerciseMaterialKey | null;
  /** The level and tier the sitting was started from: sent on the open. */
  entryContext: EntryContext;
}

/**
 * The interaction family of the dev launcher's sitting. The real play route reads it from
 * the tier's package.
 */
export const PLAY_INTERACTION_FAMILY: InteractionFamily = 'performed';

/** The authored packages, by key: the figure each tier of the server's content plays. */
const SEEDED_PACKAGES: ReadonlyMap<string, SeedExercisePackage> = new Map(
  scalesPerformanceRead.packages.map((pkg) => [pkg.key, pkg]),
);

/**
 * Resolve a tapped level (and optional tier and key) of the server's content into a
 * playable sitting, or `null` when it does not resolve: an unknown level, a level with
 * no cells or tiers, a package this client has no figure for, or a progress policy it
 * cannot apply.
 *
 * `tierNumber` and `tonic` are optional: a missing or unknown tier plays the level's
 * lowest tier, and a missing key or one the level does not hold plays the level's first
 * cell, so bad or partial route params degrade to the level's easiest entry rather than
 * a crash.
 */
export function resolvePlaySession(
  content: ExerciseContent,
  levelNumber: number,
  tierNumber?: number,
  tonic?: string,
): ResolvedPlaySession | null {
  const level = content.levels.find((lv) => lv.level_number === levelNumber);
  if (!level) return null;

  const tapped = tonic != null ? level.cells.find((c) => c.tonic === (tonic as Tonic)) : undefined;
  const cell = tapped ?? level.cells[0];
  if (!cell) return null;

  const tiers = [...level.tiers].sort((a, b) => a.tier_number - b.tier_number);
  const requested =
    tierNumber != null ? tiers.find((t) => t.tier_number === tierNumber) : undefined;
  const tier = requested ?? tiers[0];
  if (!tier) return null;

  const config = waypointConfig(tier.waypoint);
  if (!config) return null;

  const pkg = SEEDED_PACKAGES.get(tier.package_key);
  if (!pkg || pkg.prompt.figure !== 'stepwise_run') return null;

  const clearancePolicy = toProgressPolicy(content.clearance_policy);
  const masteryPolicy = toProgressPolicy(content.mastery_policy);
  if (!clearancePolicy || !masteryPolicy) return null;

  return {
    prompt: pkg.prompt,
    exercise: {
      id: content.id,
      slug: content.slug,
      clearance_policy: clearancePolicy,
      mastery_policy: masteryPolicy,
    },
    cell: { scale: cell.scale, tonic: cell.tonic },
    config,
    interaction_family: pkg.interaction_family,
    materialKey: {
      packageKey: tier.package_key,
      levelNumber: level.level_number,
      tierNumber: tier.tier_number,
    },
    entryContext: { level: level.level_number, tier: tier.tier_number },
  };
}
